import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type Theme = 'light' | 'dark' | 'system';
type ChartType = 'candle' | 'line' | 'area';

interface SettingsState {
  theme: Theme;
  chartType: ChartType;
  showVolume: boolean;
  showIndicators: boolean;
  defaultIndicators: string[];
  refreshInterval: number; // in seconds
  /** User-chosen K-line section height (px). `null` = viewport-responsive. */
  klineHeight: number | null;
  /** ChipRail width in px — user-adjustable, clamped [48, 200]. */
  chipRailWidth: number;
  /** Default visible K bars on first load / period switch. Fetch量 != 视觉窗。 */
  defaultVisibleBars: number;
  notifications: {
    priceAlert: boolean;
    newsAlert: boolean;
    predictionAlert: boolean;
  };
}

interface SettingsActions {
  setTheme: (theme: Theme) => void;
  setChartType: (chartType: ChartType) => void;
  toggleVolume: () => void;
  toggleIndicators: () => void;
  setDefaultIndicators: (indicators: string[]) => void;
  setRefreshInterval: (interval: number) => void;
  setKlineHeight: (height: number | null) => void;
  setChipRailWidth: (width: number) => void;
  setDefaultVisibleBars: (bars: number) => void;
  updateNotifications: (notifications: Partial<SettingsState['notifications']>) => void;
  reset: () => void;
}

export const CHIP_RAIL_WIDTH_MIN = 48;
export const CHIP_RAIL_WIDTH_MAX = 480;
export const CHIP_RAIL_WIDTH_DEFAULT = 120;
export const DEFAULT_VISIBLE_BARS_MIN = 30;
export const DEFAULT_VISIBLE_BARS_MAX = 500;
export const DEFAULT_VISIBLE_BARS_DEFAULT = 180;

const defaultSettings: SettingsState = {
  theme: 'system',
  chartType: 'candle',
  showVolume: true,
  showIndicators: true,
  defaultIndicators: ['MA', 'EMA', 'MACD', 'RSI'],
  refreshInterval: 60,
  klineHeight: null,
  chipRailWidth: CHIP_RAIL_WIDTH_DEFAULT,
  defaultVisibleBars: DEFAULT_VISIBLE_BARS_DEFAULT,
  notifications: {
    priceAlert: true,
    newsAlert: true,
    predictionAlert: true,
  },
};

export const useSettingsStore = create<SettingsState & SettingsActions>()(
  persist(
    (set) => ({
      ...defaultSettings,

      setTheme: (theme) => {
        set({ theme });
        // Apply theme to document
        if (theme === 'dark') {
          document.documentElement.classList.add('dark');
        } else if (theme === 'light') {
          document.documentElement.classList.remove('dark');
        } else {
          // System preference
          const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
          document.documentElement.classList.toggle('dark', prefersDark);
        }
      },

      setChartType: (chartType) => set({ chartType }),

      toggleVolume: () => set((state) => ({ showVolume: !state.showVolume })),

      toggleIndicators: () => set((state) => ({ showIndicators: !state.showIndicators })),

      setDefaultIndicators: (indicators) => set({ defaultIndicators: indicators }),

      setRefreshInterval: (interval) => set({ refreshInterval: interval }),

      setKlineHeight: (height) => set({ klineHeight: height }),

      setChipRailWidth: (width) => set({
        chipRailWidth: Math.max(CHIP_RAIL_WIDTH_MIN, Math.min(CHIP_RAIL_WIDTH_MAX, Math.round(width))),
      }),

      setDefaultVisibleBars: (bars) => set({
        defaultVisibleBars: Math.max(DEFAULT_VISIBLE_BARS_MIN, Math.min(DEFAULT_VISIBLE_BARS_MAX, Math.round(bars))),
      }),

      updateNotifications: (notifications) =>
        set((state) => ({
          notifications: { ...state.notifications, ...notifications },
        })),

      reset: () => set(defaultSettings),
    }),
    {
      name: 'settings-storage',
    }
  )
);

// Initialize theme on load
if (typeof window !== 'undefined') {
  const stored = localStorage.getItem('settings-storage');
  if (stored) {
    const { state } = JSON.parse(stored);
    if (state?.theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else if (state?.theme === 'system') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      document.documentElement.classList.toggle('dark', prefersDark);
    }
  }
}
